import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Joyride, { ACTIONS, EVENTS, type CallBackProps, type TooltipRenderProps } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { canAccessPath } from '../app/access';
import { useAuthStore } from '../stores/authStore';
import { useTourStore } from '../stores/tourStore';
import { categoriesSteps } from './categoriesSteps';
import { designerCabinetSteps, designerCreateSteps, designerPayoutsSteps, designerStatsSteps, designerTariffsSteps } from './designer';
import { designsSteps } from './designsSteps';
import { fullCycleSteps } from './fullCycleSteps';
import {
  designCreateSteps,
  designEditSteps,
  designImportSteps,
  designerPdfUploadSteps,
  designerThemeCreateSteps,
  designerThemeNewSteps,
  designerThemeTaskSteps,
  designerThemeUploadSteps,
  productCreateSteps,
  productEditSteps,
} from './modals';
import { ozonSteps } from './ozonSteps';
import { layoutCreateSteps, layoutDuplicateSteps, layoutHelpSteps, layoutJobSteps, layoutJobsSteps, layoutStudioSteps } from './layoutGenerator';
import { productsSteps } from './productsSteps';
import { isRouteAllowed, isTourAllowed } from './access';
import { normalizeTourPath, resolveTourForPath, type ModalTourName, type TourName, type TourStep, type WorkflowTourName } from './types';
import { wildberriesSteps } from './wildberriesSteps';

const TOUR_STEPS: Partial<Record<TourName, TourStep[]>> = {
  products: productsSteps,
  wildberries: wildberriesSteps,
  ozon: ozonSteps,
  designs: designsSteps,
  categories: categoriesSteps,
  'designer-create': designerCreateSteps,
  'designer-stats': designerStatsSteps,
  'designer-payouts': designerPayoutsSteps,
  'designer-tariffs': designerTariffsSteps,
  'layout-jobs': layoutJobsSteps,
  'layout-create': layoutCreateSteps,
  'layout-studio': layoutStudioSteps,
  'layout-job': layoutJobSteps,
  'layout-duplicate': layoutDuplicateSteps,
  'layout-help': layoutHelpSteps,
};

/**
 * Экраны генератора меняются по ходу работы (этап студии, шаг мастера, есть ли ошибка),
 * поэтому их тур собирается из того, что есть на экране в момент запуска: иначе счётчик
 * «3 / 20» обещал бы шаги, которых пользователь не увидит.
 */
const ADAPTIVE_TOURS: ReadonlySet<TourName> = new Set([
  'layout-jobs', 'layout-create', 'layout-studio', 'layout-job', 'layout-duplicate', 'layout-help',
]);

/** Параметр адреса, которым справка просит открыть экран сразу с туром: `?tour=layout-jobs`. */
export const TOUR_QUERY_PARAM = 'tour';

/**
 * Запуск тура на экране, который ещё догружается (переход по ссылке из справки): ждём, пока
 * найдутся все якоря или их число перестанет расти, — шапка и строки списка приходят в разное время.
 * Не дольше `timeoutMs`: дальше тур запускается с тем, что успело появиться.
 */
function startWhenAnchorsSettle(tour: TourName, timeoutMs = 8000): void {
  const steps = TOUR_STEPS[tour] ?? [];
  const startedAt = Date.now();
  let lastCount = -1;
  let stableTicks = 0;
  const tick = () => {
    const count = presentSteps(steps).length;
    stableTicks = count === lastCount ? stableTicks + 1 : 0;
    lastCount = count;
    // Все якоря на месте — сразу; иначе ждём 2 с без изменений: часть шагов на этом экране может просто не появиться.
    if (count === steps.length || (count > 1 && stableTicks >= 8) || Date.now() - startedAt > timeoutMs) {
      useTourStore.getState().startTour(tour);
      return;
    }
    window.setTimeout(tick, 250);
  };
  window.setTimeout(tick, 250);
}

function presentSteps(steps: TourStep[]): TourStep[] {
  if (typeof document === 'undefined') return steps;
  return steps.filter((step) => {
    if (step.target === 'body') return true;
    try {
      // Элемент может быть в разметке, но скрыт (свёрнутый блок, пустая плашка) — такой шаг joyride не покажет.
      const element = document.querySelector(step.target);
      return element !== null && element.getClientRects().length > 0;
    } catch {
      return false;
    }
  });
}

const MODAL_TOUR_STEPS: Record<ModalTourName, TourStep[]> = {
  'product-create': productCreateSteps,
  'product-edit': productEditSteps,
  'design-create': designCreateSteps,
  'design-edit': designEditSteps,
  'design-import': designImportSteps,
  'designer-theme-create': designerThemeCreateSteps,
  'designer-theme-new': designerThemeNewSteps,
  'designer-theme-task': designerThemeTaskSteps,
  'designer-theme-upload': designerThemeUploadSteps,
  'designer-pdf-upload': designerPdfUploadSteps,
};

const WORKFLOW_TOUR_STEPS: Record<WorkflowTourName, TourStep[]> = {
  'full-cycle': fullCycleSteps,
  'designer-cabinet': designerCabinetSteps,
};

const CustomTooltip: React.FC<TooltipRenderProps> = ({
  continuous, index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps, isLastStep, size,
}) => (
  <div {...tooltipProps} className="max-w-md overflow-hidden rounded-xl border border-zinc-700 bg-zinc-900 text-zinc-100 shadow-2xl">
    <div className="bg-gradient-to-r from-indigo-700 to-indigo-600 px-5 py-3">
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-lg font-semibold text-white">{step.title || 'Подсказка'}</h3>
        <span className="rounded bg-indigo-500/30 px-2 py-0.5 text-sm font-medium text-indigo-100">{index + 1} / {size}</span>
      </div>
    </div>
    <div className="px-5 py-4 text-sm leading-relaxed text-zinc-200">{step.content}</div>
    <div className="flex items-center justify-between border-t border-zinc-700 bg-zinc-950 px-5 py-3">
      <div>{index > 0 && <button {...backProps} className="rounded-lg px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800">← Назад</button>}</div>
      <div className="flex items-center gap-2">
        <button {...skipProps} className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100">Пропустить</button>
        {continuous
          ? <button {...primaryProps} className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-500">{isLastStep ? 'Завершить' : 'Далее →'}</button>
          : <button {...closeProps} className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-500">Закрыть</button>}
      </div>
    </div>
  </div>
);

export const TourProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { activeTour, currentStepIndex, isRunning, handleJoyrideCallback, goToStep, endTour } = useTourStore();
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationPendingRef = useRef(false);
  const context = useMemo(() => ({ role: user?.role, permissions: user?.permissions }), [user?.role, user?.permissions]);
  const currentPath = normalizeTourPath(location.pathname);
  const isModalTour = Boolean(activeTour && activeTour in MODAL_TOUR_STEPS);
  const isWorkflowTour = Boolean(activeTour && activeTour in WORKFLOW_TOUR_STEPS);

  const steps = useMemo<TourStep[]>(() => {
    if (!activeTour) return [];
    if (activeTour in WORKFLOW_TOUR_STEPS) return WORKFLOW_TOUR_STEPS[activeTour as WorkflowTourName];
    if (activeTour in MODAL_TOUR_STEPS) return MODAL_TOUR_STEPS[activeTour as ModalTourName];
    const pageSteps = TOUR_STEPS[activeTour as TourName] ?? [];
    return ADAPTIVE_TOURS.has(activeTour as TourName) ? presentSteps(pageSteps) : pageSteps;
  }, [activeTour]);

  // Запуск по ссылке из справки: параметр снимается сразу, чтобы обновление страницы не запускало тур снова.
  const requestedTour = new URLSearchParams(location.search).get(TOUR_QUERY_PARAM);
  useEffect(() => {
    if (!requestedTour) return;
    const params = new URLSearchParams(location.search);
    params.delete(TOUR_QUERY_PARAM);
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
    if (resolveTourForPath(location.pathname) !== requestedTour) return;
    // Ожидание не снимается при смене адреса: снятие параметра выше само меняет адрес и иначе отменило бы запуск.
    startWhenAnchorsSettle(requestedTour as TourName);
  }, [location.hash, location.pathname, location.search, navigate, requestedTour]);

  const navigateForTour = useCallback((rawTarget: string): boolean => {
    const target = normalizeTourPath(rawTarget);
    if (!context.permissions || !canAccessPath(target, context.permissions) || !isRouteAllowed(target, context)) {
      console.warn(`[TourProvider] Переход тура на "${target}" запрещён`);
      endTour();
      return false;
    }
    navigationPendingRef.current = true;
    navigate(target);
    return true;
  }, [context, endTour, navigate]);

  useEffect(() => {
    if (activeTour && isRunning && !isTourAllowed(activeTour, context)) endTour();
  }, [activeTour, context, endTour, isRunning]);

  useEffect(() => {
    if (!isWorkflowTour || !isRunning) return;
    const target = steps[currentStepIndex]?.navigateTo;
    if (target && normalizeTourPath(target) !== currentPath) navigateForTour(target);
  }, [currentPath, currentStepIndex, isRunning, isWorkflowTour, navigateForTour, steps]);

  useEffect(() => {
    if (!navigationPendingRef.current || !isRunning) return;
    const timer = window.setTimeout(() => {
      navigationPendingRef.current = false;
      const helpers = (window as Window & { __joyrideHelpers?: { update?: (options: { run: boolean }) => void } }).__joyrideHelpers;
      helpers?.update?.({ run: true });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [currentPath, isRunning]);

  const handleCallback = useCallback((data: CallBackProps) => {
    if (isWorkflowTour && data.type === EVENTS.STEP_AFTER) {
      const nextIndex = data.action === ACTIONS.NEXT ? data.index + 1 : data.index - 1;
      const nextStep = steps[nextIndex];
      if (nextStep?.navigateTo && normalizeTourPath(nextStep.navigateTo) !== currentPath) {
        if (navigateForTour(nextStep.navigateTo)) {
          window.setTimeout(() => {
            goToStep(nextIndex);
            navigationPendingRef.current = false;
          }, 700);
        }
        return;
      }
    }
    handleJoyrideCallback(data);
  }, [currentPath, goToStep, handleJoyrideCallback, isWorkflowTour, navigateForTour, steps]);

  const styles = useMemo(() => ({
    options: {
      arrowColor: '#18181b', backgroundColor: '#18181b', overlayColor: 'rgba(0, 0, 0, 0.62)',
      primaryColor: isWorkflowTour ? '#059669' : '#4f46e5', textColor: '#e4e4e7', zIndex: isModalTour ? 100000 : 10000,
    },
    spotlight: { borderRadius: '8px' },
    overlay: { zIndex: isModalTour ? 99999 : 9999 },
    beacon: { display: 'none' }, beaconInner: { display: 'none' }, beaconOuter: { display: 'none' },
  }), [isModalTour, isWorkflowTour]);

  return <>
    {children}
    <Joyride
      steps={steps} stepIndex={currentStepIndex} run={isRunning} continuous scrollToFirstStep showProgress showSkipButton
      disableScrolling={false} scrollOffset={100} spotlightPadding={8} callback={handleCallback} styles={styles}
      locale={{ back: 'Назад', close: 'Закрыть', last: 'Завершить', next: 'Далее', open: 'Открыть', skip: 'Пропустить' }}
      tooltipComponent={CustomTooltip}
      getHelpers={(helpers) => { (window as Window & { __joyrideHelpers?: typeof helpers }).__joyrideHelpers = helpers; }}
      floaterProps={{ styles: { floater: { zIndex: isModalTour ? 100001 : 10001 } } }}
    />
  </>;
};

export default TourProvider;
